import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEvent, deleteEvent, getEventById, updateEvent } from '../../services/api';
import { getToken } from '../../services/auth';
import RichTextEditor from '../../components/RichTextEditor';

const FORMAT_OPTIONS = [
  { value: 'in_person', label: 'In-person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
];

function AdminEventForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState('');

  // Helper to get current date/time with default time of 12:00 PM
  const getDefaultDateTime = () => {
    const now = new Date();
    now.setHours(12, 0, 0, 0); // Set to 12:00 PM
    const offsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  // Helper to get end date (start date + 1 hour)
  const getDefaultEndDateTime = () => {
    const now = new Date();
    now.setHours(13, 0, 0, 0); // Set to 1:00 PM (12:00 PM + 1 hour)
    const offsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    name: '',
    description: '',
    start_date: mode === 'create' ? getDefaultDateTime() : '',
    end_date: mode === 'create' ? getDefaultEndDateTime() : '',
    location: '',
    capacity: 0,
    event_format: 'in_person',
    online_url: '',
    base_price: '0.00',
    pro_member_price: '0.00',
    early_bird_deadline: '',
    early_bird_price: '',
    early_bird_pro_price: '',
    is_active: true,
    is_published: false,
    speaker: '',
    speaker_picture_url: '',
  });
  const [speakerPictureFile, setSpeakerPictureFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [removeSpeakerPicture, setRemoveSpeakerPicture] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toPriceInput = useCallback((value, fallback = '0.00') => {
    if (value === null || value === undefined || value === '') return fallback;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return fallback;
    return numeric.toFixed(2);
  }, []);

  const toLocalDateTimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  useEffect(() => {
    if (mode === 'edit' && id) {
      const load = async () => {
        try {
          setLoading(true);
          const ev = await getEventById(id);
          const basePrice = toPriceInput(ev.base_price, '0.00');
          const proPrice = toPriceInput(
            ev.pro_member_price ?? ev.base_price ?? basePrice,
            basePrice
          );

          setForm({
            name: ev.name || '',
            description: ev.description || '',
            start_date: toLocalDateTimeInput(ev.start_date),
            end_date: toLocalDateTimeInput(ev.end_date),
            location: ev.location || '',
            capacity: ev.capacity || 0,
            event_format: ev.event_format || 'in_person',
            online_url: ev.online_url || '',
            base_price: basePrice,
            pro_member_price: proPrice,
            early_bird_deadline: toLocalDateTimeInput(ev.early_bird_deadline),
            early_bird_price: toPriceInput(ev.early_bird_price, ''),
            early_bird_pro_price: toPriceInput(ev.early_bird_pro_price, ''),
            is_active: !!ev.is_active,
            is_published: !!ev.is_published,
            speaker: ev.speaker || '',
            speaker_picture_url: ev.speaker_picture || '',
          });
          setExistingImages(ev.images || []);
          setSpeakerPictureFile(null);
          setGalleryFiles([]);
          setRemovedImageIds([]);
          setRemoveSpeakerPicture(false);
        } catch (e) {
          setError(e.message || 'Failed to load event');
        } finally {
          setLoading(false);
        }
      };
      load();
    } else {
      setExistingImages([]);
      setRemovedImageIds([]);
      setRemoveSpeakerPicture(false);
    }
  }, [mode, id, toPriceInput]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    // For datetime-local inputs, default time to 12:00 PM if time is midnight (00:00)
    if (type === 'datetime-local' && value) {
      // Check if time is set to midnight (00:00)
      if (value.endsWith('T00:00') || value.length === 10) {
        // Replace with 12:00 PM or append if only date
        const datePart = value.slice(0, 10);
        finalValue = datePart + 'T12:00';
      }
    }

    // If start_date is being changed, automatically set end_date to start_date + 1 hour
    if (name === 'start_date' && finalValue) {
      const startDate = new Date(finalValue);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      const offsetMs = endDate.getTimezoneOffset() * 60000;
      const endDateStr = new Date(endDate.getTime() - offsetMs).toISOString().slice(0, 16);

      setForm(prev => ({ ...prev, [name]: finalValue, end_date: endDateStr }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: finalValue }));
  };

  const onDateTimeBlur = (e) => {
    const { name, value } = e.target;
    if (!value) return;

    // When user leaves the field, check if time needs adjustment
    const currentTime = new Date();
    const selectedTime = new Date(value);

    // If no time was set (defaults to 00:00), or if time matches current time (browser auto-fill),
    // set it to 12:00 PM
    const timeStr = value.slice(11, 16); // Extract HH:MM
    const currentTimeStr = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;

    // Check if time is 00:00 or matches current time (within 1 minute)
    const isDefaultTime = timeStr === '00:00' || timeStr === currentTimeStr ||
                         Math.abs(selectedTime - currentTime) < 120000; // Within 2 minutes

    if (isDefaultTime && timeStr !== '12:00') {
      const datePart = value.slice(0, 10);
      const adjustedValue = datePart + 'T12:00';

      // If start_date is being adjusted, also update end_date to start + 1 hour
      if (name === 'start_date') {
        const startDate = new Date(adjustedValue);
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
        const offsetMs = endDate.getTimezoneOffset() * 60000;
        const endDateStr = new Date(endDate.getTime() - offsetMs).toISOString().slice(0, 16);

        setForm(prev => ({ ...prev, [name]: adjustedValue, end_date: endDateStr }));
      } else {
        setForm(prev => ({ ...prev, [name]: adjustedValue }));
      }
    } else if (name === 'start_date') {
      // Even if time doesn't need adjustment, update end_date to start + 1 hour
      const startDate = new Date(value);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      const offsetMs = endDate.getTimezoneOffset() * 60000;
      const endDateStr = new Date(endDate.getTime() - offsetMs).toISOString().slice(0, 16);

      setForm(prev => ({ ...prev, end_date: endDateStr }));
    }
  };

  const quillRef = useRef(null);

  const handleDescriptionChange = useCallback((value) => {
    setForm(prev => ({ ...prev, description: value }));
  }, []);

  const handleImageUpload = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      try {
        const token = getToken();
        const response = await fetch('/api/uploads/rich-text/', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const data = await response.json();
        if (data?.location) {
          const range = quill.getSelection(true);
          const insertIndex = range ? range.index : quill.getLength();
          quill.insertEmbed(insertIndex, 'image', data.location, 'user');
          quill.setSelection(insertIndex + 1, 0);
        } else {
          throw new Error('Upload response missing image location');
        }
      } catch (uploadError) {
        console.error('Image upload failed:', uploadError);
        setError('Image upload failed. Please try again.');
      }
    };

    input.click();
  }, []);

  const editorModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: handleImageUpload,
        htmlEditButton: () => {
          const quill = quillRef.current?.getEditor();
          if (!quill) return;

          const container = quill.root.parentElement;
          if (!container) return;

          const overlay = document.createElement('div');
          overlay.className = 'quill-html-overlayContainer';

          const popup = document.createElement('div');
          popup.className = 'quill-html-popupContainer';

          const title = document.createElement('h3');
          title.className = 'quill-html-popupTitle';
          title.innerText = 'Edit HTML Source';

          const textareawrapper = document.createElement('div');
          textareawrapper.className = 'quill-html-textContainer';

          const textarea = document.createElement('textarea');
          textarea.className = 'quill-html-textArea';
          textarea.value = quill.root.innerHTML;

          const buttongroup = document.createElement('div');
          buttongroup.className = 'quill-html-buttonGroup';

          const cancel = document.createElement('button');
          cancel.className = 'quill-html-button ql-html-buttonCancel';
          cancel.type = 'button';
          cancel.innerText = 'Cancel';

          const apply = document.createElement('button');
          apply.className = 'quill-html-button ql-html-buttonOk';
          apply.type = 'button';
          apply.innerText = 'Apply';

          cancel.addEventListener('click', () => {
            overlay.remove();
          });

          apply.addEventListener('click', () => {
            const newHtml = textarea.value;
            quill.clipboard.dangerouslyPasteHTML(newHtml);
            overlay.remove();
            setForm(prev => ({ ...prev, description: quill.root.innerHTML }));
          });

          textareawrapper.appendChild(textarea);
          buttongroup.appendChild(cancel);
          buttongroup.appendChild(apply);
          popup.appendChild(title);
          popup.appendChild(textareawrapper);
          popup.appendChild(buttongroup);
          overlay.appendChild(popup);
          container.appendChild(overlay);
        },
      },
    },
    clipboard: {
      matchVisual: false,
    },
    imageResize: {
      modules: ['Resize', 'DisplaySize'],
    },
    imageDrop: true,
    htmlEditButton: {
      debug: false,
      syntax: false,
      buttonHTML: '<div style="display:inline-block; vertical-align:middle; margin-top:-8px;">&lt;/&gt;</div>',
      msg: 'Edit HTML Source',
      okText: 'Apply',
      cancelText: 'Cancel',
    },
  }), [handleImageUpload]);

  const editorFormats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'align',
    'color', 'background',
    'link', 'image',
  ], []);

  const onSpeakerFileChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setSpeakerPictureFile(file);
    if (file) {
      setRemoveSpeakerPicture(false);
    }
  };

  const onGalleryFilesChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const availableSlots = Math.max(0, 5 - existingImages.length);

    if (availableSlots <= 0) {
      setError('Maximum of 5 gallery images reached. Remove an existing image before uploading more.');
      setGalleryFiles([]);
      return;
    }

    if (selected.length > availableSlots) {
      setError(`You can upload up to ${availableSlots} additional image(s).`);
    }

    setGalleryFiles(selected.slice(0, availableSlots));
  };

  const onCancel = () => navigate('/admin/events');

  const onSubmit = (e) => {
    e.preventDefault();
    const submit = async () => {
      try {
        setSaving(true);
        setError('');

        const basePriceValue = Number(form.base_price);
        const proPriceValue = Number(form.pro_member_price);

        if (Number.isNaN(basePriceValue) || basePriceValue < 0) {
          setSaving(false);
          setError('Base price must be zero or greater.');
          return;
        }

        if (Number.isNaN(proPriceValue) || proPriceValue < 0) {
          setSaving(false);
          setError('Pro member price must be zero or greater.');
          return;
        }

        const formattedBasePrice = basePriceValue.toFixed(2);
        const formattedProPrice = proPriceValue.toFixed(2);

        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description || '');

        if (form.start_date) {
          formData.append('start_date', new Date(form.start_date).toISOString());
        }

        if (form.end_date) {
          formData.append('end_date', new Date(form.end_date).toISOString());
        }

        formData.append('location', form.location || '');
        formData.append('capacity', String(form.capacity ?? 0));
        formData.append('event_format', form.event_format);
        formData.append('online_url', form.online_url || '');
        formData.append('base_price', formattedBasePrice);
        formData.append('pro_member_price', formattedProPrice);

        // Early bird pricing fields
        if (form.early_bird_deadline) {
          formData.append('early_bird_deadline', new Date(form.early_bird_deadline).toISOString());
        }
        if (form.early_bird_price) {
          const earlyBirdPrice = Number(form.early_bird_price);
          if (!Number.isNaN(earlyBirdPrice) && earlyBirdPrice >= 0) {
            formData.append('early_bird_price', earlyBirdPrice.toFixed(2));
          }
        }
        if (form.early_bird_pro_price) {
          const earlyBirdProPrice = Number(form.early_bird_pro_price);
          if (!Number.isNaN(earlyBirdProPrice) && earlyBirdProPrice >= 0) {
            formData.append('early_bird_pro_price', earlyBirdProPrice.toFixed(2));
          }
        }

        formData.append('is_active', String(!!form.is_active));
        formData.append('is_published', String(!!form.is_published));
        formData.append('speaker', form.speaker || '');

        if (speakerPictureFile) {
          formData.append('speaker_picture', speakerPictureFile);
        } else if (removeSpeakerPicture) {
          formData.append('clear_speaker_picture', '1');
        }

        if (galleryFiles.length) {
          galleryFiles.forEach((file) => {
            formData.append('gallery_images', file);
          });
        }

        if (removedImageIds.length) {
          formData.append('deleted_image_ids', removedImageIds.join(','));
        }

        const response = mode === 'edit'
          ? await updateEvent(id, formData)
          : await createEvent(formData);

        const successMessage = mode === 'edit'
          ? 'Event updated successfully.'
          : 'Event created successfully.';

        const targetId = response?.id || id;

        navigate('/admin/events', {
          state: {
            success: successMessage,
            lastEditedEventId: targetId,
          },
        });
      } catch (submitError) {
        setError(submitError.message || 'Failed to save event');
      } finally {
        setSaving(false);
      }
    };

    submit();
  };

  const handleRemoveExistingImage = (imageId) => {
    setExistingImages((prev) => prev.filter((image) => image.id !== imageId));
    setRemovedImageIds((prev) => (prev.includes(imageId) ? prev : [...prev, imageId]));
  };

  const handleRemoveSpeakerPicture = () => {
    setForm((prev) => ({ ...prev, speaker_picture_url: '' }));
    setSpeakerPictureFile(null);
    setRemoveSpeakerPicture(true);
  };

  const handleDelete = async () => {
    if (!id) return;
    const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError('');
      await deleteEvent(id);
      navigate('/admin/events', {
        state: {
          success: 'Event deleted successfully.',
        },
      });
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">{mode === 'edit' ? 'Edit Event' : 'New Event'}</h1>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-3">
          {error.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <form onSubmit={onSubmit} className="bg-white p-4 rounded shadow space-y-4 max-w-3xl">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input name="name" value={form.name} onChange={onChange} className="w-full border rounded px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <div className="rich-text-editor">
              <RichTextEditor
                ref={quillRef}
                value={form.description}
                onChange={handleDescriptionChange}
                modules={editorModules}
                formats={editorFormats}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input type="datetime-local" name="start_date" value={form.start_date} onChange={onChange} onBlur={onDateTimeBlur} className="w-full border rounded px-3 py-2" required />
              <p className="text-xs text-gray-500 mt-1">Time defaults to 12:00 PM if not specified</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input type="datetime-local" name="end_date" value={form.end_date} onChange={onChange} onBlur={onDateTimeBlur} className="w-full border rounded px-3 py-2" />
              <p className="text-xs text-gray-500 mt-1">Auto-set to start date + 1 hour</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Format</label>
              <select
                name="event_format"
                value={form.event_format}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
              >
                {FORMAT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input
                name="location"
                value={form.location}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
                placeholder={form.event_format === 'online' ? 'Optional for online events' : 'Event venue'}
              />
            </div>
          </div>

          {['online', 'hybrid'].includes(form.event_format) && (
            <div>
              <label className="block text-sm font-medium mb-1">Online URL</label>
              <input
                name="online_url"
                value={form.online_url}
                onChange={onChange}
                className="w-full border rounded px-3 py-2"
                placeholder="https://..."
              />
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Regular Pricing</h2>
            <p className="text-sm text-gray-600 mb-3">
              All registrants receive the community price by default. Pro pricing automatically applies to both quarterly and annual pro memberships.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Community/Base Price (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="base_price"
                  value={form.base_price}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pro Member Price (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="pro_member_price"
                  value={form.pro_member_price}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Early Bird Pricing (Optional)</h2>
            <p className="text-sm text-gray-600 mb-3">
              Set early bird pricing with a deadline. Leave fields blank to disable early bird pricing.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Early Bird Deadline</label>
              <input
                type="datetime-local"
                name="early_bird_deadline"
                value={form.early_bird_deadline}
                onChange={onChange}
                onBlur={onDateTimeBlur}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">Registration must be completed before this date/time for early bird pricing</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Early Bird Community Price (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="early_bird_price"
                  value={form.early_bird_price}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Leave blank to disable"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Early Bird Pro Member Price (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="early_bird_pro_price"
                  value={form.early_bird_pro_price}
                  onChange={onChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Leave blank to disable"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Featured Speaker</label>
            <input
              name="speaker"
              value={form.speaker}
              onChange={onChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Speaker name"
            />
            <div className="mt-2">
              <label className="block text-sm font-medium mb-1">Speaker Photo</label>
              {form.speaker_picture_url && (
                <div className="mb-2 flex items-center gap-3">
                  <img src={form.speaker_picture_url} alt="Current speaker" className="w-16 h-16 rounded-full object-cover border" />
                  <button
                    type="button"
                    className="text-sm text-red-600 hover:text-red-700"
                    onClick={handleRemoveSpeakerPicture}
                    disabled={saving}
                  >
                    Remove photo
                  </button>
                </div>
              )}
              <input type="file" accept="image/*" onChange={onSpeakerFileChange} />
              {speakerPictureFile && (
                <p className="text-xs text-gray-500 mt-1">Selected: {speakerPictureFile.name}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Event Gallery</label>
            {existingImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {existingImages.map(image => (
                  <div key={image.id} className="relative group">
                    <img src={image.image} alt={image.caption || 'Event gallery image'} className="w-full h-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingImage(image.id)}
                      className="absolute inset-0 bg-black bg-opacity-50 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      disabled={saving}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input type="file" accept="image/*" multiple onChange={onGalleryFilesChange} />
            <p className="text-xs text-gray-500 mt-1">Upload up to 5 images. Existing images remain unless replaced.</p>
            {galleryFiles.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">{galleryFiles.length} new image(s) selected.</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Capacity</label>
              <input type="number" min="0" name="capacity" value={form.capacity} onChange={onChange} className="w-full border rounded px-3 py-2" />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input id="is_active" type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
              <label htmlFor="is_active">Active</label>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input id="is_published" type="checkbox" name="is_published" checked={form.is_published} onChange={onChange} />
              <label htmlFor="is_published">Published</label>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {mode === 'edit' && (
              <button
                type="button"
                className="px-4 py-2 border border-red-200 text-red-600 rounded hover:bg-red-50"
                onClick={handleDelete}
                disabled={saving || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Event'}
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button type="button" className="px-4 py-2 border rounded" onClick={onCancel} disabled={saving || deleting}>Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-blue-400" disabled={saving || deleting}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default AdminEventForm;
